import { v4 as uuid } from 'uuid';
import {
  IGizmo,
  IGizmoCallbacks,
  IGizmoEvent,
  IGizmoFeatures,
  IPlatformCallbacks,
  IRootGizmoCallbacks,
  IRootGizmoData,
  TJsonData,
} from '../../types';
import { findGizmos } from '../utils';
import Gizmo from '../gizmo';
import RootGizmo from '../root-gizmo';
import { Subject } from 'rxjs';
import { Dao } from '../../dao';
import { FlowyFoundation } from '../../flowy';
import SectionStateMachine from '../section-state-machine';
import { jsonToFlowy } from './json';

interface IParams {
  config: IGizmo;
  parentRootGizmoFid?: string;
  data?: IRootGizmoData;
  jsonData?: TJsonData;
  context: {
    rootGizmo: RootGizmo;
    callbacks: IGizmoCallbacks;
    rootGizmoCallbacks: IRootGizmoCallbacks;
    platformCallbacks?: IPlatformCallbacks;
    foundation?: FlowyFoundation;
    gizmos: (Gizmo | RootGizmo)[];
    events: Subject<IGizmoEvent>;
    dao?: Dao;
    params: any;
    setData: (data: IRootGizmoData) => void;
    defaultFeatures: IGizmoFeatures;
    setRootGizmoDataCreate: (value: boolean) => void;
  };
}

interface IReturn {
  gizmos: (Gizmo | RootGizmo)[];
  features: IGizmoFeatures;
  sectionStateMachine?: SectionStateMachine;
  createRootGizmoData: boolean;
}

function init({
  config,
  parentRootGizmoFid,
  data,
  jsonData,
  context,
}: IParams): Promise<IReturn> {
  return new Promise<IReturn>(async resolve => {
    const {
      callbacks,
      rootGizmoCallbacks,
      rootGizmo,
      gizmos,
      events,
      platformCallbacks,
      foundation,
      dao,
      params,
      setData,
      defaultFeatures,
      setRootGizmoDataCreate,
    } = context;
    const { gizmos: gizmoConfigs } = config;
    const features = defaultFeatures;
    // It will be set to true when root gizmo data is created, a.k.a., new form.
    let createRootGizmoData = false;

    if (jsonData !== undefined && data === undefined && config.gizmos) {
      // Right now we're only supporting no nested gizmos
      data = jsonToFlowy({ config, jsonData });
    }

    // This arrray is used so the Gizmo can now it's path to the parent RootGizmo
    const ancestors = [
      {
        fid: config.fid,
        uid: config.fid,
      },
    ];

    if (!data) {
      const rootGizmoDataFid = uuid();
      const rootGizmoConfig = rootGizmo.getConfig();
      const rootGizmoData: IRootGizmoData = {
        fid: rootGizmoDataFid,
        filledFormId: rootGizmoDataFid,
        formId: rootGizmoConfig.fid,
        formVersion: rootGizmoConfig.version,
        rootGizmoFid: rootGizmoConfig.fid,
        rootGizmoVersion: rootGizmoConfig.version!,
        d: {},
        version: 1,
      };

      if (parentRootGizmoFid) {
        rootGizmoData.belongsTo = {
          formId: params['parent-root-gizmo-fid'],
          // TODO: this has to go? Do a test saving in the server, I believe it's populated
          // TODO: by the backend.
          // id: params['parent-data-id'],
          filledFormId: params['parent-data-fid'],
          answerId: params['parent-gizmo-fid'],
        };
      }

      data = rootGizmoData;
      createRootGizmoData = true;
    } else if (data?.d && Object.keys(data.d).length === 0) {
      // We're adding this check because we have a scenario where the server is going to respond
      // with a RootGizmo data that has no data. Right now this happens when we have root gizmo\
      // data paired with a user (social programs project).
      createRootGizmoData = true;
    }
    data.epoch_start = Math.floor(Date.now() / 1000);
    setRootGizmoDataCreate(createRootGizmoData);
    setData(data);

    if (gizmoConfigs) {
      for (let i = 0; i < gizmoConfigs.length; i++) {
        const gizmoConfig = gizmoConfigs[i];

        if (gizmoConfig.type === 'view' || gizmoConfig.type === 'form') {
          const rootGizmo = new RootGizmo({
            config: gizmoConfig,
            callbacks: rootGizmoCallbacks,
            foundation,
            params,
            platformCallbacks,
            dao,
          });
          await rootGizmo.init();

          gizmos.push(rootGizmo);
        } else if (gizmoConfig.type === 'section') {
          // Sections can only live directly bellow a RootGizmo
          const gizmo = new Gizmo({
            config: gizmoConfig,
            callbacks,
            events,
            platformCallbacks,
            foundation,
            dao,
            params,
            ancestors,
            rootGizmoCallbacks,
          });

          if (i === 0) {
            gizmo.setFeatures({
              singleFeatures: [
                {
                  path: 'section.first',
                  value: true,
                },
                {
                  path: 'section.active',
                  value: true,
                },
              ],
            });
          } else if (i === gizmoConfigs.length - 1) {
            gizmo.setFeatures({
              singleFeatures: [
                {
                  path: 'section.last',
                  value: true,
                },
                {
                  path: 'section.active',
                  value: false,
                },
              ],
            });
          } else {
            gizmo.setFeatures({
              singleFeatures: [
                {
                  path: 'section.middle',
                  value: true,
                },
                {
                  path: 'section.active',
                  value: false,
                },
              ],
            });
          }
          await gizmo.init();

          gizmos.push(gizmo);
        } else {
          const gizmo = new Gizmo({
            config: gizmoConfig,
            callbacks,
            rootGizmoCallbacks,
            events,
            platformCallbacks,
            dao,
            params,
            ancestors,
            foundation,
          });

          if (i === 0) {
            gizmo.setFeatures({
              singleFeatures: [{ path: 'firstInSection', value: true }],
            });
          }
          if (i === gizmoConfigs.length - 1) {
            gizmo.setFeatures({
              singleFeatures: [{ path: 'lastInSection', value: true }],
            });
          }

          await gizmo.init();

          gizmos.push(gizmo);
        }
      }

      // Right now we're going to check if the form has a button with a 'form.save'
      // operation. In case it doesn't we're going to add it
      if (config.type === 'form') {
        // In here we're going to check if the form is used as signup, if that's the case we're
        // going to add password and password confirmation fields
        if (config.ops?.form?.auth?.usernameGizmoUid) {
          const passwordGizmoConfig: IGizmo = {
            fid: 'added_password',
            type: 'text_field',
            label: 'Contraseña',
            uid: 'password',
            ops: {
              display: {
                isDisplayed: false,
              },
              required: {
                isRequired: true,
              },
              textField: {
                password: {
                  isPassword: true,
                },
              },
            },
          };
          const passwordConfirmationConfig: IGizmo = {
            fid: 'added_password_confirmation',
            type: 'text_field',
            label: 'Confirmar contraseña',
            uid: 'password_confirmation',
            ops: {
              required: {
                isRequired: true,
              },
              display: {
                isDisplayed: false,
              },
              textField: {
                password: {
                  isPassword: true,
                },
              },
              localValidation: {
                errorMessage: 'Las contraseñas no coinciden',
                flux: '{{password}} == {{password_confirmation}}',
              },
            },
          };

          const passwordGizmo = new Gizmo({
            config: passwordGizmoConfig,
            callbacks,
            rootGizmoCallbacks,
            events,
            dao,
            params,
            ancestors,
            foundation,
          });
          await passwordGizmo.init();
          const passwordConfirmationGizmo = new Gizmo({
            config: passwordConfirmationConfig,
            callbacks,
            rootGizmoCallbacks,
            events,
            dao,
            params,
            ancestors,
            foundation,
          });
          await passwordConfirmationGizmo.init();

          const lastGizmo = gizmos[gizmos.length - 1];

          // If the last gizmo is a section we're going to add the password fields before there
          if (
            lastGizmo.getConfig().type === 'section' &&
            lastGizmo.getGizmos() !== undefined
          ) {
            const backButtonIndex = lastGizmo
              .getGizmos()
              ?.findIndex(
                g => g.getConfig().ops?.button?.operation === 'section.back'
              );

            if (backButtonIndex !== undefined) {
              lastGizmo
                .getGizmos()
                ?.splice(backButtonIndex, 0, passwordConfirmationGizmo);
              lastGizmo.getGizmos()?.splice(backButtonIndex, 0, passwordGizmo);
            }
          } else {
            gizmos.push(passwordGizmo);
            gizmos.push(passwordConfirmationGizmo);
          }
        }

        const buttonGizmos = findGizmos({
          gizmo: rootGizmo,
          type: 'button',
        });
        const foundSaveButton = buttonGizmos.find(
          g => g.getConfig().ops?.button?.operation === 'form.submit'
        );

        if (!foundSaveButton && params['m'] !== 's') {
          const label = config.ops?.form?.saveButtonLabel || 'Enviar';
          const submitButtonConfig: IGizmo = {
            fid: 'added_submit_button',
            type: 'button',
            label,
            uid: 'added_submit_button',
            ops: {
              button: {
                operation: 'form.submit',
              },
            },
          };
          const submitButton = new Gizmo({
            config: submitButtonConfig,
            callbacks,
            rootGizmoCallbacks,
            events,
            dao,
            params,
            ancestors,
            foundation,
          });
          await submitButton.init();

          gizmos.push(submitButton);
        }

        if (config.ops?.form?.displayCancelButton?.isDisplayed !== false) {
          const foundCancelButton = buttonGizmos.find(
            g => g.getConfig().ops?.button?.operation === 'form.cancel'
          );

          if (!foundCancelButton) {
            const label = config.ops?.form?.cancelButtonLabel || 'Cancelar';
            const cancelButtonConfig: IGizmo = {
              fid: 'added_cancel_button',
              type: 'button',
              label,
              uid: 'added_cancel_button',
              ops: {
                button: {
                  operation: 'form.cancel',
                  confirmation: {
                    title: '¿Estás seguro?',
                    description: 'Se perderán los cambios',
                  },
                },
              },
            };
            const cancelButton = new Gizmo({
              config: cancelButtonConfig,
              callbacks,
              rootGizmoCallbacks,
              events,
              dao,
              params,
              ancestors,
              foundation,
            });
            await cancelButton.init();

            gizmos.push(cancelButton);
          }
        }
      }
    }

    if (config.ops?.displayLabel === false) {
      features.displayLabel = false;
    }

    let sectionStateMachine: SectionStateMachine | undefined;
    const sectionPresent = gizmos.find(g => g.getConfig().type === 'section');

    if (sectionPresent) {
      sectionStateMachine = new SectionStateMachine({
        rootGizmo,
      });
      await sectionStateMachine.init();
    }

    resolve({ gizmos, features, sectionStateMachine, createRootGizmoData });
  });
}

export default init;
